import { createContext, useContext, useMemo, type ReactNode } from 'react';

import type { Glossary } from '@/shared/api/types';

import { createMusicFormatter, type MusicFormatter, type NoteNaming } from './format';

/** Leads from a term in a text to its entry in the glossary. */
export type TermLink = (termKey: string) => ReactNode;

const FormatterContext = createContext<MusicFormatter>(createMusicFormatter(undefined, null));
const TermLinkContext = createContext<TermLink | null>(null);
const TermMarksContext = createContext(true);

interface MusicI18nProviderProps {
  glossary: Glossary | undefined;
  /** The learner's preferred naming system; null follows the language's default. */
  naming: NoteNaming | null;
  /** Shown under the hint of a term. Without it, hints have no link. */
  termLink?: TermLink;
  /** Whether terms are set apart in texts and explain themselves. On unless the learner turned it off. */
  termMarks?: boolean;
  children: ReactNode;
}

/**
 * Supplies the term formatter to everything below. The glossary, the naming
 * preference and the way to the glossary page are passed in by the app layer, so
 * this module depends on neither the API client, the settings store nor the router.
 */
export function MusicI18nProvider({
  glossary,
  naming,
  termLink,
  termMarks = true,
  children,
}: MusicI18nProviderProps) {
  const formatter = useMemo(() => createMusicFormatter(glossary, naming), [glossary, naming]);
  return (
    <FormatterContext value={formatter}>
      <TermLinkContext value={termLink ?? null}>
        <TermMarksContext value={termMarks}>{children}</TermMarksContext>
      </TermLinkContext>
    </FormatterContext>
  );
}

export function useMusicFormatter(): MusicFormatter {
  return useContext(FormatterContext);
}

export function useTermLink(): TermLink | null {
  return useContext(TermLinkContext);
}

/** Whether terms are marked in texts at all (the learner's choice). */
export function useTermMarks(): boolean {
  return useContext(TermMarksContext);
}
